import {
    addTopicToChecklistWithClauses,
    getAllClauses,
    getTopic,
    getTopicsForChecklist,
    getTopicsWithClausesForChecklist,
    IApiAxiosError,
    IClause,
    IClauseChecklist,
    IEnabledClauseSection,
    updateEnabledClausesForTopics,
} from "@/api";
import { TableFilterToolbar } from "@/components/admin/table/TableFilterToolbar";
import { Modal } from "@/components/shared/Modal";
import { Table } from "@/components/shared/table/Table";
import { PolicyTypeCell } from "@/components/shared/table/cells/PolicyTypeCell";
import { useFilterModel } from "@/hooks/useFilterModel";
import { Chip, Stack, Typography } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";

const columns: GridColDef<IClause>[] = [
    {
        field: "title",
        headerName: "Clause Name",
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) => (
            <Typography variant="body2" noWrap>
                {row.title}
            </Typography>
        ),
    },
    {
        field: "topics",
        headerName: "Topic",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => {
            if (!row.topics || row.topics.length === 0) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        No topic
                    </Typography>
                );
            }
            // Show first topic, with count if multiple
            const primaryTopic = row.topics[0];
            return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" noWrap>
                        {primaryTopic.name}
                    </Typography>
                    {row.topics.length > 1 && (
                        <Chip
                            label={`+${row.topics.length - 1}`}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                    )}
                </Stack>
            );
        },
    },
    {
        field: "policy_type",
        headerName: "Policy Type",
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
            <PolicyTypeCell policyType={row.policy_type} />
        ),
    },
];

interface IProps {
    /**
     * Whether the modal is open.
     */
    open: boolean;
    /**
     * Handler to close the modal.
     */
    onClose: () => void;
    /**
     * The clause checklist to add a clause to.
     */
    clauseChecklist: IClauseChecklist;
}

/**
 * A modal for adding a single clause to a checklist.
 * When a clause is added, its topic is also added (if not already present).
 */
export const AddClauseToChecklistDialog = ({
    open,
    onClose,
    clauseChecklist,
}: IProps) => {
    const [selectedClause, setSelectedClause] = useState<IClause | null>(null);
    const [rows, setRows] = useState<IClause[]>([]);

    const { filterModel, handleFilterModelChange, resetFilterModel } =
        useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const queryClient = useQueryClient();

    // Fetch assigned topics to check if clause's topic already exists
    const { data: assignedTopics = [] } = useQuery({
        queryKey: ["admin-clause-checklist-topics", clauseChecklist.id],
        queryFn: () => getTopicsForChecklist(clauseChecklist.id),
    });

    const assignedTopicIds = useMemo(
        () => new Set(assignedTopics.map((t) => t.id)),
        [assignedTopics]
    );

    const {
        mutate,
        isPending: mutationPending,
        error: mutationError,
        reset,
    } = useMutation({
        mutationFn: async () => {
            if (!selectedClause || selectedClause.topics.length === 0) {
                throw new Error("Selected clause has no assigned topic");
            }

            // Use the first topic from the clause
            const targetTopic = selectedClause.topics[0];
            const topicId = targetTopic.id;

            // Fetch topic detail to get topic_clause_id and sections
            const topicDetail = await getTopic(topicId);

            // Find the clause in the topic's sections to get topic_clause_id
            let topicClauseId: number | null = null;
            const clauseSections: (number | null)[] = [];

            for (const section of topicDetail.clauses_by_section) {
                const clauseInSection = section.clauses.find(
                    (c) => c.clause_id === selectedClause.id
                );
                if (clauseInSection) {
                    topicClauseId = clauseInSection.topic_clause_id;
                    clauseSections.push(
                        section.document_section_type?.id ?? null
                    );
                }
            }

            if (topicClauseId === null) {
                throw new Error("Clause not found in topic");
            }

            // Build enabled_clauses for this clause's sections
            const newEnabledClauses: IEnabledClauseSection[] =
                clauseSections.map((sectionId) => ({
                    topic_clause_id: topicClauseId!,
                    document_section_type_id: sectionId,
                }));

            // Check if topic already exists in checklist
            const topicExists = assignedTopicIds.has(topicId);

            if (!topicExists) {
                // Add topic with only this clause enabled
                await addTopicToChecklistWithClauses(
                    clauseChecklist.id,
                    topicId,
                    newEnabledClauses
                );
            } else {
                // Topic exists - merge this clause into existing enabled clauses
                const topicsWithClauses =
                    await getTopicsWithClausesForChecklist(clauseChecklist.id);
                const existingTopic = topicsWithClauses.find(
                    (t) => t.id === topicId
                );

                // If topic not found in details, this is a data consistency error
                if (!existingTopic) {
                    throw new Error(
                        `Topic ${topicId} not found in checklist details`
                    );
                }

                let mergedEnabledClauses: IEnabledClauseSection[];

                if (existingTopic.enabled_clauses.length === 0) {
                    // All clauses are already enabled (empty array = all enabled)
                    // The clause is already included, nothing to do
                    throw new Error(
                        "This clause is already enabled in the checklist"
                    );
                } else {
                    // Merge new clause-sections into existing enabled list
                    const existingKeys = new Set(
                        existingTopic.enabled_clauses.map(
                            (ec) =>
                                `${ec.topic_clause_id}:${ec.document_section_type_id}`
                        )
                    );

                    mergedEnabledClauses = [...existingTopic.enabled_clauses];

                    for (const newEc of newEnabledClauses) {
                        const key = `${newEc.topic_clause_id}:${newEc.document_section_type_id}`;
                        if (!existingKeys.has(key)) {
                            mergedEnabledClauses.push(newEc);
                        }
                    }

                    // Update with merged clauses
                    await updateEnabledClausesForTopics(clauseChecklist.id, {
                        updates: [
                            {
                                topic_id: topicId,
                                enabled_clauses: mergedEnabledClauses,
                            },
                        ],
                    });
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-checklist-topics", clauseChecklist.id],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "admin-clause-checklist-details",
                    String(clauseChecklist.id),
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "admin-clause-checklist-topics-with-clauses",
                    clauseChecklist.id,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "clause-checklist-relevance-status",
                    clauseChecklist.id,
                ],
            });
            enqueueSnackbar(
                "Clause has been successfully added to the checklist.",
                {
                    variant: "success",
                }
            );
            handleClose();
        },
    });

    // Fetch clauses from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-clause-checklist-add-clause",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getAllClauses(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
            reset();
            setSelectedClause(null);
        }
    }, [data, reset]);

    // Memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    const handleRowClick = (params: GridRowParams<IClause>) => {
        // Single selection via row click
        if (selectedClause?.id === params.row.id) {
            // Clicking selected row deselects it
            setSelectedClause(null);
        } else {
            setSelectedClause(params.row);
        }
    };

    const handleClose = () => {
        onClose();
        setSelectedClause(null);
        reset();
        resetFilterModel();
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    // Check if selected clause has a topic
    const selectedClauseHasTopic =
        selectedClause && selectedClause.topics.length > 0;
    const selectedClauseTopic = selectedClauseHasTopic
        ? selectedClause.topics[0]
        : null;

    const axiosError = mutationError as IApiAxiosError;

    const footerText = () => {
        if (axiosError) {
            return (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ fontSize: "0.75rem" }}
                >
                    {axiosError.response?.data?.detail ||
                        axiosError.message ||
                        "Failed to add clause to the checklist."}
                </Typography>
            );
        } else if (selectedClause) {
            if (selectedClauseHasTopic) {
                return (
                    <Typography variant="caption" color="text.primary">
                        Adding &quot;{selectedClause.title}&quot; will{" "}
                        {assignedTopicIds.has(selectedClauseTopic!.id)
                            ? `enable it in the existing "${selectedClauseTopic!.name}" topic.`
                            : `also add the "${selectedClauseTopic!.name}" topic.`}
                    </Typography>
                );
            } else {
                return (
                    <Typography variant="caption" color="warning.dark">
                        This clause has no assigned topic and cannot be added.
                    </Typography>
                );
            }
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add Clause to Checklist"
            subtitle="Select a clause to add. Its topic will be automatically included."
            maxWidth="md"
            isLoading={mutationPending}
            actions={[
                {
                    label: "Add Clause",
                    onClick: () => mutate(),
                    variant: "contained",
                    color: "primary",
                    disabled: !selectedClauseHasTopic || mutationPending,
                },
            ]}
            footerText={footerText()}
        >
            <Stack spacing={2}>
                <Stack height={400}>
                    <Table
                        loading={isFetching}
                        columns={columns}
                        rows={rows}
                        onRowClick={handleRowClick}
                        getRowClassName={(params) =>
                            params.row.id === selectedClause?.id
                                ? "Mui-selected"
                                : ""
                        }
                        pageSizeOptions={[10, 25, 50]}
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        onPaginationModelChange={setPaginationModel}
                        paginationMode="server"
                        filterMode="server"
                        sortingMode="server"
                        onFilterModelChange={handleFilterModelChange}
                        onSortModelChange={handleFilterModelChange}
                        showToolbar
                        slots={{
                            toolbar: TableFilterToolbar,
                        }}
                        sx={{
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },
                            "& .MuiDataGrid-row.Mui-selected": {
                                bgcolor: "action.selected",
                            },
                            "& .MuiDataGrid-row.Mui-selected:hover": {
                                bgcolor: "action.selected",
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Modal>
    );
};
